import { useState, useEffect } from 'react';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';

import BasicInfo from './components/BasicInfo';
import BasicStats from './components/BasicStats';
import InfluencerDetails from './components/InfluencerDetails';
import AudienceDetails from './components/AudienceDetails';
import PopularPosts from './components/PopularPosts';
import CommercialPosts from './components/CommercialPosts/CommercialPosts';
import RecentPosts from './components/RecentPosts/RecentPosts';
import TopReels from './components/TopReels/TopReels';
import RecentReels from './components/RecentReels/RecentReels';
import TopHashTags from './components/TopHashTags/TopHashTags';
import StatsFilters from './components/BasicStats/StatsFilters';
import RelevantHashTags from './components/RelevantHashTags/RelevantHashTags';
import { SectionCard, ui } from './components/shared/insightUi';
import ProfileSkeleton, { StatTilesSkeleton } from './components/shared/ProfileSkeleton';
import { ThemeProvider } from '@material-ui/core/styles';
import {
  getIdViaChannelYt,
  // getDiscoveryInfluencersDetails,
  getIdViaHandler,
} from 'api_handlers/discovery';
import {
  getDiscoveryInfluencersDetails,
  brandAffinityPost,
} from 'api_handlers/planner2';
import themeV2 from 'themeV2';
import mixpanel from 'utils/mixpanel';

function DetailViewModal({ id, detailViewOpened = false, setDetailViewOpened, is_staff }) {
  const [influenerDetail, setInfluenerDetail] = useState({});
  const [isClicked, setIsclicked] = useState(false);
  const [updateStatus, setUpdateStatus] = useState('');
  const [getId, setGetId] = useState(id);
  const influencer_handler = window.location.pathname.split('/')[4];
  const platform = window.location.pathname.split('/')[3];
  const [filteredData, setFilteredData] = useState({});
  // True once the first post-wise stats response has landed — even an empty
  // one — so the KPI skeleton can't get stuck on profiles with no post data.
  const [statsLoaded, setStatsLoaded] = useState(false);
  const [brandAffinityList, setBrandAffinityList] = useState([]);
  const [brandPosts, setBrandPosts] = useState([]);
  const [period, setPeriod] = useState('28');
  const [contentType, setContentType] = useState('overall');
  const [filterViaTypes, setFilterViaTypes] = useState('all');

  const dataSharingEnabled =
    String(influenerDetail?.bc_public_data?.dataSharingState || '').toUpperCase().includes('ENABLED') ||
    String(influenerDetail?.bc_public_data?.dataSharingState || '').toUpperCase().includes('GIVEN');

  const fetchDetails = async () => {
    if (getId) {
      getDiscoveryInfluencersDetails(getId).then((response) => {
        setInfluenerDetail(response);
        if (response?.update_status === 'updating') {
          setUpdateStatus('updating');
        }
        if (response?.update_status === 'updated') {
          setUpdateStatus('updated');
        }
      });
    }
  };

  useEffect(() => {
    const fetchBrandPost = async () => {
      if (brandAffinityList.length === 0) {
        setBrandPosts([]);
        return;
      }
      try {
        const response = await brandAffinityPost({
          planner_id: getId,
          keywords: brandAffinityList,
        });

        const posts = response?.brand_affinity_posts || [];
        setBrandPosts([...posts]);
      } catch (err) {
        console.error('Failed to fetch brand posts:', err);
      }
    };

    fetchBrandPost();
  }, [brandAffinityList, getId]);

  if (!id) {
    is_staff = true;
    detailViewOpened = true;
    if (platform === 'youtube') {
      getIdViaChannelYt(influencer_handler).then((res) => {
        setGetId(res.data.id);
      });
    } else if (platform === 'instagram') {
      getIdViaHandler(influencer_handler).then((res) => {
        setGetId(res.data.id);
      });
    }
  }

  useEffect(() => {
    fetchDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getId]);

  useEffect(() => {
    if (isClicked === true) {
      fetchDetails().then(() => {
        setIsclicked(false);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClicked]);

  // Standalone profile page only. Inside the Insightframe iframe the parent already
  // tracks the view, and as a modal `id` is passed in.
  useEffect(() => {
    if (id || window.self !== window.top) return;
    const params = new URLSearchParams(window.location.search);
    mixpanel.track('Insights viewed', {
      url: window.location.pathname,
      type: params.get('type') || 'direct',
      source: params.get('src') || 'direct',
      platform,
      handle: influencer_handler,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (platform === 'youtube' && influenerDetail.is_brandconnect !== 'yes') {
      setContentType('overall');
    }
  }, [platform, influenerDetail.is_brandconnect]);

  return (
    <ThemeProvider theme={themeV2}>
      <div
        style={{
          background: ui.pageBg,
          minHeight: '100vh',
          padding: '24px 20px 56px',
          fontFamily: ui.font,
          textAlign: 'left',
        }}
      >
        <div
          style={{
            maxWidth: 1240,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 24,
          }}
        >
          {!influenerDetail.id ? (
            <ProfileSkeleton />
          ) : (
            <>
          <BasicInfo
            {...influenerDetail}
            is_staff={is_staff}
            isClicked={(val) => setIsclicked(val)}
            updateStatus={updateStatus === 'updating' ? true : false}
            getId={getId}
            period={period}
          />

          <SectionCard
            icon={<BarChartRoundedIcon fontSize="small" />}
            title="Performance overview"
            subtitle="Key reach and engagement metrics"
            actions={
              <StatsFilters
                {...influenerDetail}
                platform={platform}
                filteredData={(val) => {
                  setFilteredData(val);
                  setStatsLoaded(true);
                }}
                isClicked={isClicked}
                period={period}
                setPeriod={setPeriod}
                contentType={contentType}
                setContentType={setContentType}
                onFilterTypeChange={setFilterViaTypes}
              />
            }
          >
            {statsLoaded ? (
              <BasicStats
                {...influenerDetail}
                filteredData={filteredData}
                period={period}
                contentType={contentType}
                filterViaTypes={filterViaTypes}
              />
            ) : (
              <StatTilesSkeleton count={6} />
            )}
          </SectionCard>

          {platform === 'youtube' && influenerDetail.is_brandconnect === 'yes' ? (
            <>
              {!dataSharingEnabled && (
                <InfluencerDetails
                  list={async (val) => await setBrandAffinityList(val)}
                  {...influenerDetail}
                  contentType={contentType}
                />
              )}
              <AudienceDetails
                list={async (val) => await setBrandAffinityList(val)}
                {...influenerDetail}
                is_staff={is_staff}
                period={period}
                contentType={contentType}
              />
            </>
          ) : (
            <>
              <InfluencerDetails
                list={async (val) => await setBrandAffinityList(val)}
                {...influenerDetail}
              />
              <AudienceDetails
                list={async (val) => await setBrandAffinityList(val)}
                {...influenerDetail}
                is_staff={is_staff}
                period={period}
              />
            </>
          )}

          <CommercialPosts
            brandAffinityList={brandAffinityList}
            brandPosts={brandPosts}
            {...influenerDetail}
          />
          <PopularPosts {...influenerDetail} />
          <RecentPosts {...influenerDetail} />
          {influenerDetail.youtube === null ? <TopReels {...influenerDetail} /> : null}
          {influenerDetail.youtube === null ? <RecentReels {...influenerDetail} /> : null}
          {influenerDetail.youtube === null ? <TopHashTags {...influenerDetail} /> : null}
          <RelevantHashTags {...influenerDetail} />
            </>
          )}
        </div>
      </div>
    </ThemeProvider>
  );
}

export default DetailViewModal;
